import { ENDPOINT_TEXT_MESSAGE_NAME } from '../../../modules/API/constants';
import { extractHsl } from '../base/environment/environment';
import { VIDEO_TYPE } from '../base/media/constants';
import {
    getLocalParticipant,
    getParticipantById,
    getVirtualScreenshareParticipantOwnerId,
    isLocalScreenshareParticipant,
    isRemoteScreenshareParticipant
} from '../base/participants/functions';

import { DEFAULT_COLOR } from './constants';
import logger from './logger';

/**
 * Whether a track carries a screen share.
 *
 * @param {Object} track - Redux track.
 * @returns {boolean}
 */
function _isDesktopTrack(track) {
    return track.videoType === VIDEO_TYPE.DESKTOP
        || track.mhTrack?.getVideoType?.() === VIDEO_TYPE.DESKTOP;
}

/**
 * The participant whose screen share is on the large video, if a screen
 * share is what the large video shows.
 *
 * @param {Object} state - The redux state.
 * @returns {string|undefined} The sharer's participant id.
 */
export function getStageScreenShareOwnerId(state) {
    const { participantId } = state['features/large-video'] || {};
    const participant = getParticipantById(state, participantId);
    const tracks = state['features/base/tracks'] || [];

    if (!participant) {
        return undefined;
    }
    if (isLocalScreenshareParticipant(participant)) {
        return getLocalParticipant(state)?.id;
    }
    if (isRemoteScreenshareParticipant(participant)) {
        // Virtual participant ids are track source names ('<endpoint>-v1'),
        // which getVirtualScreenshareParticipantOwnerId does not parse.
        return tracks.find(t => t.mhTrack?.getSourceName?.() === participant.id)?.participantId
            || getVirtualScreenshareParticipantOwnerId(participant.id);
    }
    if (participant.isFakeParticipant || participant.fakeParticipant) {
        return undefined;
    }

    return tracks.some(t => t.participantId === participant.id && _isDesktopTrack(t) && !t.muted)
        ? participant.id
        : undefined;
}

/**
 * Whether viewers may draw on a participant's screen share. The sharer
 * decides; it is on unless they turned it off.
 *
 * @param {Object} state - The redux state.
 * @param {string} sharerId - The sharer.
 * @returns {boolean}
 */
export function isRemoteAnnotationAllowed(state, sharerId) {
    if (sharerId === getLocalParticipant(state)?.id) {
        return state['features/screen-annotation'].allowed;
    }

    return !getParticipantById(state, sharerId)?.annotationOff;
}

/**
 * Color of a participant's strokes and name tag: their tile color, which each
 * participant announces for themselves (SET_PARTICIPANT_COLOR), so every client
 * shows the same one.
 *
 * @param {Object} state - The redux state.
 * @param {string} id - Participant id.
 * @returns {string}
 */
export function getAnnotationColor(state, id) {
    const color = getParticipantById(state, id)?.color;

    // On web the tile color is a gradient ending in the hsl() base color.
    return color ? extractHsl(color) : DEFAULT_COLOR;
}

/**
 * Broadcasts an annotation message in the envelope the desktop app reads
 * through the External API's endpointTextMessageReceived event.
 *
 * @param {Object} conference - The conference.
 * @param {string} type - ANNOTATION_MESSAGE or POINTER_MESSAGE.
 * @param {Object} payload - Message payload.
 * @returns {void}
 */
export function sendAnnotationMessage(conference, type, payload) {
    try {
        conference?.sendEndpointMessage('', {
            name: ENDPOINT_TEXT_MESSAGE_NAME,
            text: JSON.stringify({
                type,
                payload
            })
        });
    } catch (error) {
        // No bridge channel yet, or it just closed: a lost annotation is harmless.
        logger.warn('Failed to send annotation message', error);
    }
}

/**
 * Where the picture of a video element is drawn, in client coordinates.
 * object-fit letterboxes (contain) or crops (cover) the frame inside the
 * element, and CSS transforms (view zoom) move and scale the element itself;
 * getBoundingClientRect already includes the latter.
 *
 * @param {HTMLVideoElement} video - The video element.
 * @returns {Object|null} { left, top, width, height }, or null before the
 * first frame.
 */
export function getVideoContentRect(video) {
    const { videoWidth, videoHeight } = video;
    const box = video.getBoundingClientRect();

    if (!videoWidth || !videoHeight || !box.width || !box.height) {
        return null;
    }

    const fit = video.ownerDocument.defaultView.getComputedStyle(video).objectFit;

    if (fit === 'fill') {
        return box;
    }

    const scaleX = box.width / videoWidth;
    const scaleY = box.height / videoHeight;
    const scale = fit === 'cover' ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
    const width = videoWidth * scale;
    const height = videoHeight * scale;

    return {
        left: box.left + ((box.width - width) / 2),
        top: box.top + ((box.height - height) / 2),
        width,
        height
    };
}

/**
 * Validates the points of a received stroke and clamps them into the frame.
 *
 * @param {any} points - Untrusted [[x, y], ...].
 * @param {number} max - Most points accepted.
 * @returns {Array<Array<number>>|null}
 */
export function readPoints(points, max) {
    if (!Array.isArray(points) || !points.length || points.length > max) {
        return null;
    }

    const result = [];

    for (const point of points) {
        if (!Array.isArray(point) || !Number.isFinite(point[0]) || !Number.isFinite(point[1])) {
            return null;
        }
        result.push([ Math.min(1, Math.max(0, point[0])), Math.min(1, Math.max(0, point[1])) ]);
    }

    return result;
}
